import { useEffect, useState } from 'react';
import { Platform } from 'react-native';

let TrackingTransparency: any = null;

if (Platform.OS === 'ios') {
  try {
    TrackingTransparency = require('expo-tracking-transparency');
  } catch (error) {
    console.warn('[ATT] expo-tracking-transparency not available:', error);
  }
}

export type ATTStatus = 'undetermined' | 'restricted' | 'denied' | 'authorized' | 'granted' | 'unavailable';

interface UseATTReturn {
  status: ATTStatus;
  isLoading: boolean;
  hasRequestedPermission: boolean;
}

export function useAppTrackingTransparency(): UseATTReturn {
  const [status, setStatus] = useState<ATTStatus>('undetermined');
  const [isLoading, setIsLoading] = useState(true);
  const [hasRequestedPermission, setHasRequestedPermission] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'ios') {
      setStatus('unavailable');
      setIsLoading(false);
      return;
    }

    if (!TrackingTransparency) {
      console.warn('[ATT] Tracking transparency module not available, treating as unavailable');
      setStatus('unavailable');
      setIsLoading(false);
      return;
    }

    const requestATTPermission = async () => {
      try {
        console.log('[ATT] ========================================');
        console.log('[ATT] 🔐 App Tracking Transparency Check');
        console.log('[ATT] ========================================');

        const { status: existingStatus } = await TrackingTransparency.getTrackingPermissionsAsync();
        console.log('[ATT] Current tracking status:', existingStatus);

        if (existingStatus === 'undetermined') {
          console.log('[ATT] Status is undetermined, requesting permission...');

          const { status: newStatus } = await TrackingTransparency.requestTrackingPermissionsAsync();
          console.log('[ATT] User responded with status:', newStatus);

          setStatus(newStatus);
          setHasRequestedPermission(true);

          if (newStatus === 'authorized' || newStatus === 'granted') {
            console.log('[ATT] ✅ Tracking AUTHORIZED - Analytics SDKs can be initialized');
          } else {
            console.log('[ATT] ⚠️ Tracking NOT authorized - Limited analytics mode');
          }
        } else {
          console.log('[ATT] Existing status:', existingStatus);
          setStatus(existingStatus);
          setHasRequestedPermission(true);

          if (existingStatus === 'authorized' || existingStatus === 'granted') {
            console.log('[ATT] ✅ Tracking already AUTHORIZED');
          } else {
            console.log('[ATT] ⚠️ Tracking restricted or denied');
          }
        }

        console.log('[ATT] ========================================');
      } catch (error) {
        console.error('[ATT] ❌ Error checking tracking permissions:', error);
        setStatus('unavailable');
      } finally {
        setIsLoading(false);
      }
    };

    requestATTPermission();
  }, []);

  return {
    status,
    isLoading,
    hasRequestedPermission,
  };
}

export function isTrackingAuthorized(status: ATTStatus): boolean {
  return status === 'authorized' || status === 'granted' || status === 'unavailable';
}
